/**
 * Inbox queues of pending reviews — pure helpers.
 *
 * A pending review sits in one of three queues: back from snooze, main, or
 * still snoozed (server field `queue`). The inbox pages render them as
 * separate groups; Finish and Snooze advance within the queue the operator
 * is working through and never into the snoozed queue.
 */
import type { AdminReviewDoc, ReviewQueue } from "../api/admin";

export function reviewQueue(doc: Pick<AdminReviewDoc, "status" | "queue">): ReviewQueue | null {
  return doc.status === "pending" ? (doc.queue ?? "main") : null;
}

export interface SplitQueues<T> {
  returned: T[];
  main: T[];
  snoozed: T[];
}

/**
 * Split pending rows by queue, keeping the server's order inside each.
 * `openAsMain` keeps a changes-requested row in the main queue – the email
 * inbox shows it in place while the author revises; navigation never lands
 * on it (`nextInQueue` stays pending-only).
 */
export function splitByQueue<T extends Pick<AdminReviewDoc, "status" | "queue">>(
  rows: T[],
  { openAsMain = false }: { openAsMain?: boolean } = {},
): SplitQueues<T> {
  const out: SplitQueues<T> = { returned: [], main: [], snoozed: [] };
  for (const row of rows) {
    const queue = reviewQueue(row) ?? (openAsMain && row.status === "changes_requested" ? "main" : null);
    if (queue) out[queue].push(row);
  }
  return out;
}

/**
 * The review to open after finishing or snoozing `current`.
 *
 * From "Back from snooze": the next returned row, then the main queue.
 * From the main queue: the next main row only. Never a snoozed row, never
 * `current` itself. `accept` narrows further (the email inbox's topic and
 * priority filter). `undefined` means the queue is empty — go to the inbox.
 */
export function nextInQueue<T extends Pick<AdminReviewDoc, "id" | "status" | "queue">>(
  rows: T[],
  current: Pick<AdminReviewDoc, "id" | "status" | "queue">,
  accept: (row: T) => boolean = () => true,
): T | undefined {
  const from = reviewQueue(current) ?? "main";
  if (from === "snoozed") return undefined;
  const { returned, main } = splitByQueue(rows.filter((r) => r.id !== current.id && accept(r)));
  return from === "returned" ? (returned[0] ?? main[0]) : main[0];
}
